import { useMemo } from 'react'
import { z } from 'zod'

import type { ApiClient } from '@/shared/api/client'
import { useSlot } from '@/shared/registry'

/**
 * The `link` slot each area that holds a person's data fills with its part of "Download my data":
 * its label and its API path, `/{segment}/v1/me/data`. My Profile lists them (SM-15) and zips
 * their files (SM-40).
 */
export const myDataSlot = 'my-data'

/** One area's part: its label, a catalog message id, and the path that lists its files. */
export interface MyDataPart {
  readonly id: string
  readonly labelId: string
  readonly path: string
}

/** A file of a part: its name, such as `course-feedback.csv`, and the path that streams it. */
export interface MyDataFile {
  readonly name: string
  readonly path: string
}

/** Every part registered in the `my-data` slot, in registry order. */
export function useMyDataParts(): readonly MyDataPart[] {
  const links = useSlot(myDataSlot, 'link')
  return useMemo(
    () =>
      links.map((link) => ({
        id: link.id,
        labelId: link.labelId,
        path: typeof link.path === 'string' ? link.path : link.path({}),
      })),
    [links],
  )
}

const fileList = z.object({
  files: z.array(z.object({ name: z.string().min(1), path: z.string().startsWith('/') })),
})

/** Lists a part's files, `GET {path}` answering `{ files: [{ name, path }] }`. */
export async function listMyDataFiles(
  client: Pick<ApiClient, 'get'>,
  path: string,
  signal?: AbortSignal,
): Promise<readonly MyDataFile[]> {
  return fileList.parse(await client.get(path, signal)).files
}
